import { useState } from "react";
import { Banknote, TrendingUp, Zap, Clapperboard, PenLine, Scissors } from "lucide-react";

// Per-VIDEO minimum base pay (floors, never per-minute rates).
// There is no automatic duration formula: the displayed estimate is the role
// minimum, and real job rates may be set higher by the owner per job.
const ANIMATOR_MINIMUMS = { "Standard Animator": 55, "High-Quality Animator": 110 };

const MODES = [
  { id: "Standard Animator", icon: Clapperboard, label: "Standard Animator" },
  { id: "High-Quality Animator", icon: Clapperboard, label: "HQ Animator" },
  { id: "Script Writer", icon: PenLine, label: "Script Writer" },
  { id: "Video Editor", icon: Scissors, label: "Video Editor" },
];

export const PayCalculator = () => {
  const [mode, setMode] = useState("Standard Animator");
  const [seconds, setSeconds] = useState(45);

  const isAnimator = mode in ANIMATOR_MINIMUMS;
  const minimum = ANIMATOR_MINIMUMS[mode] || 0;
  const fill = ((seconds - 15) / (80 - 15)) * 100;

  return (
    <section id="pay" className="py-24 sm:py-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="text-center">
          <p className="font-mono-label text-xs font-bold uppercase tracking-widest text-cyan-600">Pay, up front</p>
          <h2 data-testid="pay-heading" className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
            No guessing. Here's the money.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
            Standard Animator — starting at $55 USD per video. High-Quality Animator — starting at $110 USD per video. Every job shows its exact pay before you claim it.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {MODES.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              data-testid={`pay-mode-${id.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMode(id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                mode === id
                  ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-cyan-300"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm sm:p-12">
          {isAnimator ? (
            <div className="flex flex-col items-center gap-10 sm:flex-row sm:gap-14">
              <div className="w-full flex-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="pay-duration" className="text-sm font-semibold text-slate-700">
                    Typical video length
                  </label>
                  <span data-testid="pay-duration-value" className="rounded-full bg-slate-100 px-3.5 py-1.5 text-sm font-bold text-slate-900">
                    {seconds}s
                  </span>
                </div>
                <input
                  id="pay-duration"
                  data-testid="pay-calculator-slider"
                  type="range"
                  min={15}
                  max={80}
                  step={5}
                  value={seconds}
                  onChange={(e) => setSeconds(Number(e.target.value))}
                  className="pay-slider mt-5 w-full"
                  style={{ "--fill": `${fill}%` }}
                />
                <div className="mt-2 flex justify-between text-[11px] font-medium text-slate-400">
                  <span>15s</span>
                  <span className="text-cyan-600">most Melto shorts</span>
                  <span>80s</span>
                </div>
              </div>

              <div className="text-center sm:text-right">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Starting at</p>
                <p data-testid="pay-estimate" className="mt-1 font-display text-5xl font-extrabold tracking-tight text-cyan-500 sm:text-6xl">
                  ${minimum}
                </p>
                <p className="mt-1 text-xs text-slate-400">per video · minimum base pay</p>
                <p className="mx-auto mt-3 max-w-xs text-xs leading-relaxed text-slate-500 sm:ml-auto">
                  Pay may increase depending on length, complexity and job requirements — the exact rate is shown on every job before you claim it.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {mode === "Script Writer" ? "Per accepted script" : "Per completed video"}
              </p>
              <p data-testid="pay-estimate" className="font-display text-5xl font-extrabold tracking-tight text-cyan-500 sm:text-6xl">
                {mode === "Script Writer" ? "$10" : "$20"}
              </p>
              <p className="max-w-md text-sm leading-relaxed text-slate-500">
                {mode === "Script Writer"
                  ? "Flat rate per accepted script — a complete, ready-to-animate storyline built for short-form Roblox pacing."
                  : "Flat rate per video. The animation and audio arrive finished — you handle the final edit to an upload-ready video, usually about an hour of work."}
              </p>
            </div>
          )}

          <div className="mt-10 grid gap-3 border-t border-slate-100 pt-8 sm:grid-cols-3">
            <div className="rounded-2xl bg-slate-50 p-5">
              <Banknote className="h-5 w-5 text-cyan-600" />
              <p className="mt-3 text-sm font-bold text-slate-900">Per video, not hourly</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">Every job shows its exact pay upfront before you take it — never below the minimum for your tier.</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-5">
              <TrendingUp className="h-5 w-5 text-cyan-600" />
              <p className="mt-3 text-sm font-bold text-slate-900">Scales with the job</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">$55 (Standard) and $110 (High-Quality) are per-video minimums — longer or more complex jobs pay more.</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-5">
              <Zap className="h-5 w-5 text-cyan-600" />
              <p className="mt-3 text-sm font-bold text-slate-900">Raises over time</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">Consistent great work = higher per-job rates as the channel grows.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
